// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import AccordionSection, { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Hidden from "@insite/mobius/Hidden";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { XNGAGE_ELEMENTS } from "@steiner/XDL/constants";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    accountLinksSections = "accountLinksSections",
    customCSS = "customCSS",
    showSignOutLink = "showSignOutLink",
}

const enum accountLinksSectionFields {
    sectionLabel = "sectionLabel",
    links = "links",
}

const enum linkFields {
    url = "url",
    linkText = "linkText",
    openInNewTab = "openInNewTab",
    addBottomBorder = "addBottomBorder",
}

type LinkModel = {
    fields: {
        url: LinkFieldValue;
        linkText: string;
        openInNewTab: boolean;
        addBottomBorder: boolean;
    };
};

type SectionModel = {
    fields: {
        sectionLabel: string;
        links: LinkModel[];
    };
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.accountLinksSections]: SectionModel[];
        [fields.customCSS]: string;
        [fields.showSignOutLink]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
    isPunchOutSession: getIsPunchOutSession(state),
    currentPage: getCurrentPage(state),
    customSettings: getSettingsCollection(state).customSettings as any,
    session: state.context.session,
});

interface ListStyleProp {}

const ListStyle = styled.ul<ListStyleProp>`
    li {
        list-style: none;
        margin-bottom: 10px;
    }
`;
const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
};
export interface MyAccountNavigationListStyles {
    sectionLabel?: TypographyPresentationProps;
    accordionStyle?: AccordionPresentationProps;
    accordionSectionStyle?: AccordionSectionPresentationProps;
    accordionLinkStyle?: LinkPresentationProps;
    container?: InjectableCss;

    separator?: InjectableCss;
    wrapper?: InjectableCss;
    mobileWrapper?: InjectableCss;
    mobileLinkWrapper?: InjectableCss;
    mobileNavWrapper?: InjectableCss;
    select?: SelectPresentationProps;
    link?: LinkPresentationProps;
}

export const myAccountNavigationListStyles: MyAccountNavigationListStyles = {};

const styles = myAccountNavigationListStyles;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export const MyAccountNavigationList = (props: Props) => {
    const { fields, history, currentPage, isPunchOutSession, cancelPunchOut, signOut, session, customSettings } = props;

    const onSignOutHandler = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    const customCssWrapper = {
        css: css`
            .navigation-list {
                .navigation-list-item {
                    list-style: none;
                }
            }
            ${props?.fields?.customCSS}
        `,
    };

    const sections = fields?.accountLinksSections;
    const isSentinelUser = session?.properties?.isSentinelUser === "true" || false;
    const isThinkificUser = session?.properties?.isThinkificUser === "true" || false;
    const sentinelURL = customSettings?.sentinelHeaderUrl;
    const thinkificlURL = customSettings?.thinkificHeaderUrl;

    const signOutElement = fields.showSignOutLink ? (
        <Link
            className="signout-clickable"
            data-test-selector="user_signout"
            aria-label="sign out"
            onClick={onSignOutHandler}
            {...styles.link}
        >
            {translate("Sign Out")}
        </Link>
    ) : null;

    if (!sections) {
        <StyledWrapper {...customCssWrapper}>
            {signOutElement}
        </StyledWrapper>;
    }

    return (
        <StyledWrapper {...customCssWrapper} {...styles.container}>
            <Hidden below="lg" {...styles.wrapper}>
                <ListStyle className="navigation-list" data-test-selector="navigationList">
                    {sections.map((section: SectionModel, index) => {
                        const sectionLabel = section.fields.sectionLabel;
                        const SectionLinks = section.fields.links;

                        return (
                            <li key={sectionLabel + index}>
                                <ul className="navigation-list-section">
                                    <li className="title" {...styles.sectionLabel}>{sectionLabel}</li>
                                    {SectionLinks.map((link: LinkModel, index) => {
                                        const fieldLink = link.fields;
                                        const linkItem = useGetLink(fieldLink.url);
                                        const linkURL = linkItem.url;

                                        if (
                                            (linkURL === sentinelURL && !isSentinelUser) ||
                                            (linkURL === thinkificlURL && !isThinkificUser)
                                        ) {
                                            return null;
                                        }

                                        if ((linkItem as any).excludeFromNavigation) {
                                            return null;
                                        }

                                        return (
                                            <li
                                                key={index}
                                                className="navigation-list-item"
                                                data-test-selector={`navigationList-${fieldLink.linkText}`}
                                            >
                                                <Link
                                                    className="navigation-list-link"
                                                    data-test-selector={`navigationList-${linkItem.title}`}
                                                    href={linkURL}
                                                    target={fieldLink.openInNewTab ? "_blank" : ""}
                                                    {...styles.link}
                                                >
                                                    {linkItem.title}
                                                </Link>
                                                {fieldLink.addBottomBorder && <StyledWrapper {...styles.separator}></StyledWrapper>}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </li>
                        );
                    })}
                </ListStyle>
                {signOutElement}
            </Hidden>
            <Hidden above="md">
                <StyledWrapper {...styles.mobileWrapper}>
                    <Accordion headingLevel={2} {...styles.accordionStyle}>
                        {sections.map((section: SectionModel, index) => {
                            const sectionLabel = section.fields.sectionLabel;
                            const SectionLinks = section.fields.links;
                            return (
                                <AccordionSection
                                    key={sectionLabel + index}
                                    title={sectionLabel}
                                    {...styles.accordionSectionStyle}
                                >

                                    <ListStyle className="navigation-list" data-test-selector="navigationList">
                                        <ul className="navigation-list-section">
                                            {SectionLinks.map((link: LinkModel, index) => {
                                                const fieldLink = link.fields;
                                                const linkItem = useGetLink(fieldLink.url);
                                                const linkURL = linkItem.url;

                                                if (
                                                    (linkURL === sentinelURL && !isSentinelUser) ||
                                                    (linkURL === thinkificlURL && !isThinkificUser)
                                                ) {
                                                    return null;
                                                }

                                                if ((linkItem as any).excludeFromNavigation) {
                                                    return null;
                                                }

                                                return (
                                                    <li
                                                        key={index}
                                                        className="navigation-list-item"
                                                        data-test-selector={`navigationList-${fieldLink.linkText}`}
                                                    >
                                                        <StyledWrapper
                                                            {...styles.mobileLinkWrapper}
                                                        >
                                                            <Link
                                                                className="navigation-list-link"
                                                                data-test-selector={`navigationList-${linkItem.title}`}
                                                                href={linkURL}
                                                                target={fieldLink.openInNewTab ? "_blank" : ""}
                                                                {...styles.link}
                                                            >
                                                                {linkItem.title}
                                                            </Link>
                                                        </StyledWrapper>
                                                </li>
                                                );
                                            })}
                                        </ul>
                                    </ListStyle>

                                
                                </AccordionSection>
                            );
                        })}
                        {
                            fields.showSignOutLink && (
                                <AccordionSection
                                    key="signout"
                                    title={translate("Sign Out")}
                                    {...styles.accordionSectionStyle}
                                >
                                    {signOutElement}
                                </AccordionSection>
                            )
                        }
                    </Accordion>
                </StyledWrapper>
            </Hidden>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.navigation-list{
}

.navigation-list-item {
}

.navigation-list-link {
}
.signout-clickable {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(MyAccountNavigationList)),
    definition: {
        group: XNGAGE_ELEMENTS,
        icon: "link-simple",
        fieldDefinitions: [
            {
                name: fields.accountLinksSections,
                editorTemplate: "ListField",
                fieldType: "Translatable",
                defaultValue: [],
                tab: basicTab,
                getDisplay: ({ fields: { sectionLabel } }) => sectionLabel,
                fieldDefinitions: [
                    {
                        name: accountLinksSectionFields.sectionLabel,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: accountLinksSectionFields.links,
                        editorTemplate: "ListField",
                        defaultValue: [],
                        tab: basicTab,
                        getDisplay: (item: HasFields, state) => {
                            const {
                                url: { type, value },
                                linkText,
                            } = item.fields;
                            if (linkText) {
                                return linkText;
                            }
                            if (type === "Page") {
                                const link = getPageLinkByNodeId(state, value);
                                return link?.title;
                            }
                            if (type === "Category") {
                                let displayValue = "";
                                const categories = (state as any).pageEditor.categories;
                                if (!categories) {
                                    displayValue = "Loading...";
                                } else {
                                    const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                                    if (matchingCategories.length === 0) {
                                        displayValue = "Unknown";
                                    } else {
                                        displayValue = matchingCategories[0].shortDescription;
                                    }
                                }

                                return displayValue;
                            }
                            if (type === "Url") {
                                return value;
                            }
                            return value;
                        },
                        fieldDefinitions: [
                            {
                                name: linkFields.url,
                                editorTemplate: "LinkField",
                                isRequired: true,
                                defaultValue: {
                                    type: "Page",
                                    value: "",
                                },
                            },
                            {
                                name: linkFields.linkText,
                                editorTemplate: "TextField",
                                defaultValue: "",
                                isRequired: false,
                                validate: (value, item) => (value.length > 40 ? "max allowed characters is 40" : null),
                            },
                            {
                                name: linkFields.openInNewTab,
                                editorTemplate: "CheckboxField",
                                defaultValue: false,
                            },
                            {
                                name: linkFields.addBottomBorder,
                                displayName: "Add Bottom Border",
                                editorTemplate: "CheckboxField",
                                defaultValue: false,
                            },
                        ],
                    },
                ],
            },
            {
                name: fields.showSignOutLink,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};
export default widgetModule;
